<template>
  <div ref="inputRef" class="w-full flex flex-col relative">
    <div class="relative" :class="context.classes.innerInput" @click="open">
      <input
        v-show="!isOpen"
        :value="context.value ? context.value[labelBy] : ''"
        :placeholder="placeholder"
        :class="context.classes.input"
        :disabled="context.disabled"
        readonly
      />
      <input
        v-show="isOpen"
        ref="searchRef"
        v-model="search"
        :placeholder="'Поиск'"
        :class="context.classes.input"
        :disabled="context.disabled"
      />

      <button @click.prevent.stop="handleDecorationClick">
        <IconChevronDown
          v-if="!context.value && !search.length"
          :class="[context.classes.decoration, { 'rotate-180': isOpen }]"
        />
        <IconX v-else :class="[context.classes.decoration]" />
      </button>
    </div>
    <Transition v-bind="fadeScale" mode="out-in">
      <ul
        v-if="isOpen"
        class="absolute flex-none top-[48px] left-0 right-0 w-full translate-y-2 max-h-[200px] overflow-y-auto z-[1000] overscroll-contain"
        :class="cardStyle"
      >
        <li
          v-for="(option, i) in resources"
          :class="[
            context.classes.option,
            { selected: context.value === option[trackBy] },
            selectionHover,
          ]"
          :key="i"
          @click="handleInput(option)"
        >
          <component
            v-if="context?.slots?.item"
            :is="context?.slots?.item"
            :option="option"
          />
          <span v-else v-text="option[labelBy]" />
        </li>
        <li
          v-if="!resources.length"
          class="pointer-events-none"
          :class="[context.classes.option]"
        >
          <component v-if="context?.slots?.empty" :is="context?.slots?.item" />
          <span v-else v-text="emptyPlaceholder" />
        </li>
      </ul>
    </Transition>
    <YandexMap
      v-if="!context.withoutMap"
      v-model="map"
      :settings="{
        location: localLocation,
        theme: context.overwriteTheme || theme,
      }"
      class="rounded overflow-hidden mt-2"
      :height="context.mapHeight || '200px'"
    >
      <YandexMapMarker
        v-if="inputValue"
        :settings="{
          coordinates: localLocation.center,
        }"
      >
        <component :is="context.slots?.marker" />
        <DefaultMarker
          v-if="!context.slots?.marker"
          :style="markerDefaultStyle"
        />
      </YandexMapMarker>

      <YandexMapDefaultFeaturesLayer />
      <YandexMapDefaultSchemeLayer />
      <YandexMapListener :settings="{ onClick: mapClick }" />
    </YandexMap>
  </div>
</template>

<script setup>
import { onClickOutside, watchDebounced } from '@vueuse/core'
import { notify } from 'notiwind'
import { computed, inject, nextTick, ref, shallowRef } from 'vue'
import {
  YandexMap,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapListener,
  YandexMapMarker,
} from 'vue-yandex-maps'

import { IconChevronDown, IconX } from '@tabler/icons-vue'

import DefaultMarker from '@/components/maps/DefaultMarker.vue'
import { fadeScale } from '@/utils/transitions.js'
import { cardStyle, selectionHover } from '@/utils/ui.js'

const props = defineProps({
  context: {
    type: Object,
    default: () => ({}),
  },
})

const inputRef = ref(null)
const searchRef = ref(null)
onClickOutside(inputRef, () => (isOpen.value = false))
const inputValue = computed({
  get() {
    return props.context.value
  },
  set(val) {
    props.context.node.input(val)
  },
})
const fetchQuery = props.context.fetchQuery || ((q) => q)
const placeholder = ref(props.context.placeholder || 'Введите адрес')
const trackBy = props.context.trackBy || 'fias_id'
const labelBy = props.context.labelBy || 'title'
const enableClick = props.context.enableClick || false
const allowRawCoordinates = props.context.allowRawCoordinates || false
const disableKladr = props.context.disableKladr || false
const settlement = props.context.settlement || null
const emptyPlaceholder = props.context.emptyPlaceholder || 'Ничего не найдено'
const fuse = ref(null)
const search = ref(
  typeof inputValue.value === 'object' && inputValue.value
    ? inputValue.value[labelBy]
    : '',
)
const isOpen = ref(false)
const http = inject('http')
const isLoading = ref(false)
const resources = ref([])
const theme = inject('theme')

const center = [68.9545, 33]
const zoom = 8
const map = shallowRef(null)

const markerDefaultStyle = {
  minWidth: '40px',
  position: 'relative',
  boxSizing: 'border-box',
  transform: 'translate(-50%, calc(-50% - 24px))',
}

watchDebounced(
  search,
  () => (search.value.length ? getSuggestions(search.value) : false),
  {
    debounce: 200,
    maxWait: 500,
  },
)

// Backend returns coordinates as strings; Yandex Maps requires finite numbers
function normalizeCoordinates(coordinates = []) {
  const normalized = [...coordinates].map(Number)

  return normalized.length === 2 && normalized.every(Number.isFinite)
    ? normalized
    : null
}

function checkInitialInput() {
  if (props.context.value?.latitude && props.context.value?.longitude) {
    const initial = normalizeCoordinates([
      props.context.value.latitude,
      props.context.value.longitude,
    ])
    if (initial) return initial
  }

  if (props.context.value?.coordinates) {
    const initial = normalizeCoordinates(props.context.value.coordinates)
    if (initial) return initial
  }

  return [...center]
}

const localLocation = shallowRef({
  center: checkInitialInput().reverse(),
  zoom: zoom,
})

function handleDecorationClick() {
  if (!inputValue.value) {
    isOpen.value = !isOpen.value
    search.value = ''
  } else {
    isOpen.value = false
    search.value = ''
    props.context.node.input(null)
  }
}

function updateLocalLocation(
  coordinates = [],
  zoom = 10,
  duration = 1000,
  other = {},
) {
  const normalized = normalizeCoordinates(coordinates)

  if (!normalized) {
    console.warn('coordinates not transmitted')
    return
  }
  localLocation.value = {
    center: normalized.reverse(),
    zoom,
    duration,
    ...other,
  }
}

function handleInput(option) {
  isOpen.value = false
  if (!props.context.withoutMap) {
    updateLocalLocation([...option.coordinates], map.value?.zoom ?? zoom)
  }
  props.context.node.input(option)
}

function open() {
  if (search.value) {
    getSuggestions(search.value)
  }
  if (!isOpen.value) {
    isOpen.value = !isOpen.value
    nextTick(() => {
      searchRef.value.focus()
    })
  }
}

let requestController = null

function getSuggestions(query) {
  if (requestController) requestController.abort()
  requestController = new AbortController()

  http('/suggestions/address', {
    method: 'GET',
    params: {
      query: fetchQuery(query),
      disableKladr: disableKladr,
      settlement: settlement
    },
    signal: requestController.signal,
  }).then(({ suggestions }) => {
    resources.value = suggestions
  })
}

function mapClick(object, event) {
  if (!enableClick) return false
  http('/suggestions/address/coords', {
    method: 'POST',
    body: {
      latitude: event.coordinates[1],
      longitude: event.coordinates[0],
    },
  }).then(({ suggestions }) => {
    if (suggestions.length) {
      handleInput(suggestions[0])
      return
    }

    if (allowRawCoordinates) {
      handleInput(rawPoint(event.coordinates))
      return
    }

    notify(
      {
        group: 'default',
        type: 'error',
        title: 'Ой! Тут ничего не нашлось',
        text: 'Попробуйте приблизить карту или найти адрес через ввод',
      },
      4000,
    )
  })
}

// Reverse geocoding may return nothing (a point in the tundra, on a lake, etc.).
// Fields that accept a bare point keep the coordinates as the value instead.
function rawPoint([longitude, latitude]) {
  const round = (value) => Number(Number(value).toFixed(6))

  return {
    fias_id: null,
    title: `${round(latitude)}, ${round(longitude)}`,
    coordinates: [round(latitude), round(longitude)],
    settlement: null,
  }
}
</script>
